import type { IntelReportWire } from '@badger-sentinel/protocol';

/**
 * The archive's states (M5 Q18).
 *
 * `GET /api/games/:gameId/intel/:turn` is a **pull**, seat-token authed, with no
 * cache on purpose — "a cached report is a report projected for whoever asked
 * first". The client had no fetcher for it at all before this phase:
 * `useSeatSocket` holds exactly one `IntelReportWire` and replaces it.
 *
 * ## Six states, where the brief asked for five
 *
 * The brief names five — *empty*, *not fetched*, *fetching*, *404 not resolved*,
 * *403* — and the sixth is the one the route's own care makes necessary:
 *
 * | State | Cause | What the rail says |
 * | --- | --- | --- |
 * | `idle` | nobody has asked for this turn | nothing — the row is a turn number |
 * | `loading` | in flight | `…` |
 * | `ready` | 200, **including a report with no entries** | the entry count, `0` and all |
 * | `absent` | 404 — that turn has not resolved yet | `NOT RESOLVED` |
 * | `forbidden` | 403 — the umpire reads the resolution log, not a report | `NO REPORT` |
 * | `failed` | anything else: the network, a 500 | the server's own words |
 *
 * **`ready` with no entries is not a sixth state and must not read like one.**
 * "A turn with no entries renders as empty, not as missing" — an empty report is
 * a statement *about a quiet turn*, and it is the whole reason `absent` is a
 * separate answer at all.
 *
 * **`failed` is not folded into `absent`**, and this is the one place this
 * console departs from `maps/pack-loader.ts`, which does fold them: for a map
 * pack, "a network error and a 404 are the same thing to a console that is
 * deciding whether it has geometry". Here they are not the same thing at all —
 * `absent` is a statement about the *game* ("that turn has not resolved yet")
 * and `failed` is a statement about the *link*. The operator's next move
 * differs, and a report that could not be fetched must never read as a turn in
 * which nothing happened.
 */
export type IntelArchiveState =
  | { readonly status: 'idle' }
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly report: IntelReportWire }
  | { readonly status: 'absent' }
  | { readonly status: 'forbidden' }
  | { readonly status: 'failed'; readonly reason: string };

export const IDLE: IntelArchiveState = Object.freeze({ status: 'idle' });

/**
 * What this console holds for one turn — **one function, three callers.**
 *
 * The reader, the delta panel and the archive rail all ask it, and Phase 6.5's
 * defect is what happens when one question is answered in two places.
 *
 * The live report is checked first and never re-fetched. It arrives on the socket
 * already projected for this seat, it is the newest turn's, and asking the route
 * for a turn already in hand would be a request whose only product is a second
 * copy of the same bytes.
 */
export function intelStateFor(
  turn: number,
  live: IntelReportWire | null,
  archive: ReadonlyMap<number, IntelArchiveState>,
): IntelArchiveState {
  if (live !== null && live.turn === turn) return { status: 'ready', report: live };
  return archive.get(turn) ?? IDLE;
}

/** The report for a turn, or null. Null covers all five non-`ready` states. */
export function reportFor(
  turn: number | null,
  live: IntelReportWire | null,
  archive: ReadonlyMap<number, IntelArchiveState>,
): IntelReportWire | null {
  if (turn === null) return null;
  const state = intelStateFor(turn, live, archive);
  return state.status === 'ready' ? state.report : null;
}

/**
 * Should this turn be asked for?
 *
 * Only from `idle`. A `failed` row is **not** retried automatically — a fetch
 * loop against a server that is refusing is a console that looks busy and is
 * doing nothing. The retry is the operator's: the row draws a control that calls
 * the fetcher directly, past this guard.
 */
export function shouldRequest(state: IntelArchiveState): boolean {
  return state.status === 'idle';
}
